import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Plus, Trash2, ArrowLeft, FileText, Download, Printer, 
  CheckCircle, Clock, AlertCircle, Euro, DollarSign, Save,
  Eye, Edit2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCIES = ['EUR', 'USD', 'GBP', 'INR', 'AUD', 'CAD', 'CHF', 'JPY', 'CNY'];

const Invoices = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get('package_id');
  const invoiceId = searchParams.get('invoice_id');
  const printRef = useRef();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  
  // Invoice form state
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_address: '',
    trip_description: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'EUR',
    notes: ''
  });
  
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0, vat_percent: 0, vat_amount: 0, total: 0 }
  ]);
  
  const [paymentData, setPaymentData] = useState({
    payment_status: 'not_paid',
    amount_paid: 0,
    payment_date: '',
    payment_notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [packageId, invoiceId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch invoice settings
      const settingsRes = await axios.get(`${API}/invoices/settings`, { headers });
      setInvoiceSettings(settingsRes.data);
      
      if (invoiceId) {
        // View/edit existing invoice
        const invoiceRes = await axios.get(`${API}/invoices/${invoiceId}`, { headers });
        setExistingInvoice(invoiceRes.data.invoice);
        setInvoiceSettings(invoiceRes.data.settings);
        
        const inv = invoiceRes.data.invoice;
        setFormData({
          client_name: inv.client_name,
          client_email: inv.client_email,
          client_address: inv.client_address || '',
          trip_description: inv.trip_description,
          invoice_date: inv.invoice_date,
          due_date: inv.due_date,
          currency: inv.currency,
          notes: inv.notes || ''
        });
        setLineItems(inv.line_items.length > 0 ? inv.line_items : [
          { description: '', quantity: 1, unit_price: 0, vat_percent: 0, vat_amount: 0, total: 0 }
        ]);
        setPaymentData({
          payment_status: inv.payment_status,
          amount_paid: inv.amount_paid,
          payment_date: inv.payment_date || '',
          payment_notes: inv.payment_notes || ''
        });
      } else if (packageId) {
        // Check if invoice exists for package
        const checkRes = await axios.get(`${API}/invoices/package/${packageId}/check`, { headers });
        if (checkRes.data.has_invoice) {
          // Redirect to view existing invoice
          navigate(`/app/invoices?invoice_id=${checkRes.data.invoice.invoice_id}`);
          return;
        }
        
        // Fetch package data
        const packageRes = await axios.get(`${API}/packages/${packageId}`, { headers });
        setPackageData(packageRes.data);
        
        // Pre-fill form with package data
        setFormData(prev => ({
          ...prev,
          client_name: packageRes.data.client_name || '',
          client_email: packageRes.data.client_email || '',
          trip_description: packageRes.data.package_name || `Package ${packageRes.data.package_id}`,
          currency: packageRes.data.currency || 'EUR'
        }));
        
        // Pre-fill line item with package sale value
        if (packageRes.data.sale_value) {
          setLineItems([{
            description: packageRes.data.package_name || 'Tour Package',
            quantity: 1,
            unit_price: packageRes.data.sale_value,
            vat_percent: 0,
            vat_amount: 0,
            total: packageRes.data.sale_value
          }]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const calculateLineItem = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const vatAmount = subtotal * (item.vat_percent / 100);
    const total = subtotal + vatAmount;
    return { ...item, vat_amount: vatAmount, total };
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    updated[index] = calculateLineItem(updated[index]);
    setLineItems(updated);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, vat_percent: 0, vat_amount: 0, total: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const totalVat = lineItems.reduce((sum, item) => sum + item.vat_amount, 0);
    const grandTotal = lineItems.reduce((sum, item) => sum + item.total, 0);
    return { subtotal, totalVat, grandTotal };
  };

  const handleSave = async () => {
    if (!formData.client_name || !formData.client_email || lineItems.length === 0) {
      toast.error(t('invoice.fillRequired'));
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      const { subtotal, totalVat, grandTotal } = calculateTotals();
      
      const invoiceData = {
        ...formData,
        package_id: packageId || existingInvoice?.package_id,
        line_items: lineItems,
        subtotal,
        total_vat: totalVat,
        grand_total: grandTotal
      };
      
      if (existingInvoice) {
        await axios.put(`${API}/invoices/${existingInvoice.invoice_id}`, invoiceData, { headers });
        toast.success(t('invoice.updated'));
        setIsEditing(false);
        fetchData();
      } else {
        const res = await axios.post(`${API}/invoices`, invoiceData, { headers });
        toast.success(t('invoice.created'));
        navigate(`/app/invoices?invoice_id=${res.data.invoice_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentUpdate = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API}/invoices/${existingInvoice.invoice_id}/payment`,
        paymentData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('invoice.paymentUpdated'));
      setShowPaymentDialog(false);
      fetchData();
    } catch (error) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    
    setDownloadingPdf(true);
    try {
      // Hide buttons and non-printable elements temporarily
      const buttonsToHide = document.querySelectorAll('.print\\:hidden');
      buttonsToHide.forEach(el => el.style.display = 'none');
      
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      // Restore hidden elements
      buttonsToHide.forEach(el => el.style.display = '');
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      const fileName = existingInvoice?.invoice_number 
        ? `Invoice-${existingInvoice.invoice_number}.pdf`
        : `Invoice-${packageData?.package_id || 'draft'}.pdf`;
      
      pdf.save(fileName);
      toast.success('Invoice downloaded as PDF');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const config = {
      paid: { variant: 'default', icon: CheckCircle, label: t('invoice.paid'), color: 'text-green-600' },
      partial: { variant: 'secondary', icon: Clock, label: t('invoice.partial'), color: 'text-yellow-600' },
      not_paid: { variant: 'destructive', icon: AlertCircle, label: t('invoice.notPaid'), color: 'text-red-600' }
    };
    const c = config[status] || config.not_paid;
    const Icon = c.icon;
    return (
      <Badge variant={c.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${c.color}`} />
        {c.label}
      </Badge>
    );
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const { subtotal, totalVat, grandTotal } = calculateTotals();

  if (loading) {
    return <div className="text-muted-foreground">{t('common.loading')}</div>;
  }

  // View mode for existing invoice
  if (existingInvoice && !isEditing) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/app/sales')}>
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{t('common.back')}</span>
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">{existingInvoice.invoice_number}</h1>
              <p className="text-sm text-muted-foreground">{t('invoice.viewInvoice')}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {getPaymentStatusBadge(existingInvoice.payment_status)}
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Edit2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('common.edit')}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={downloadingPdf}>
              {downloadingPdf ? (
                <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">{downloadingPdf ? 'Generating...' : t('invoice.downloadPdf')}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('invoice.print')}</span>
            </Button>
          </div>
        </div>

        {/* Invoice Document */}
        <Card className="max-w-4xl mx-auto" ref={printRef}>
          <CardContent className="p-4 sm:p-6 lg:p-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between gap-4 sm:gap-8 mb-6 sm:mb-8">
              <div>
                {invoiceSettings?.company_logo_url && (
                  <img src={invoiceSettings.company_logo_url} alt="Logo" className="h-12 sm:h-16 mb-2 sm:mb-4" />
                )}
                <h2 className="text-lg sm:text-xl font-bold">{invoiceSettings?.company_name}</h2>
                <p className="text-xs sm:text-sm text-muted-foreground whitespace-pre-line">{invoiceSettings?.company_address}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{invoiceSettings?.company_email}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{invoiceSettings?.company_phone}</p>
                {invoiceSettings?.tax_number && (
                  <p className="text-xs sm:text-sm text-muted-foreground">{invoiceSettings?.tax_label}: {invoiceSettings?.tax_number}</p>
                )}
              </div>
              <div className="sm:text-right">
                <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">{t('invoice.invoice')}</h1>
                <p className="font-medium text-sm sm:text-base">{existingInvoice.invoice_number}</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                  {t('invoice.date')}: {existingInvoice.invoice_date}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t('invoice.dueDate')}: {existingInvoice.due_date}
                </p>
              </div>
            </div>

            {/* Bill To */}
            <div className="mb-8 p-4 bg-muted/50 rounded-lg">
              <h3 className="font-semibold mb-2">{t('invoice.billTo')}</h3>
              <p className="font-medium">{existingInvoice.client_name}</p>
              <p className="text-sm text-muted-foreground">{existingInvoice.client_email}</p>
              {existingInvoice.client_address && (
                <p className="text-sm text-muted-foreground whitespace-pre-line">{existingInvoice.client_address}</p>
              )}
            </div>

            {/* Trip Description */}
            <div className="mb-6">
              <h3 className="font-semibold mb-2">{t('invoice.tripDescription')}</h3>
              <p>{existingInvoice.trip_description}</p>
            </div>

            {/* Line Items */}
            <Table className="mb-6">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">{t('invoice.description')}</TableHead>
                  <TableHead className="text-right">{t('invoice.qty')}</TableHead>
                  <TableHead className="text-right">{t('invoice.unitPrice')}</TableHead>
                  <TableHead className="text-right">{t('invoice.vatPercent')}</TableHead>
                  <TableHead className="text-right">{t('invoice.vatAmount')}</TableHead>
                  <TableHead className="text-right">{t('invoice.total')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {existingInvoice.line_items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.unit_price, existingInvoice.currency)}</TableCell>
                    <TableCell className="text-right">{item.vat_percent}%</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.vat_amount, existingInvoice.currency)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(item.total, existingInvoice.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('invoice.subtotal')}</span>
                  <span>{formatCurrency(existingInvoice.subtotal, existingInvoice.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('invoice.totalVat')}</span>
                  <span>{formatCurrency(existingInvoice.total_vat, existingInvoice.currency)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>{t('invoice.grandTotal')}</span>
                  <span className="text-primary">{formatCurrency(existingInvoice.grand_total, existingInvoice.currency)}</span>
                </div>
                {existingInvoice.payment_status === 'partial' && (
                  <>
                    <div className="flex justify-between text-green-600">
                      <span>{t('invoice.amountPaid')}</span>
                      <span>{formatCurrency(existingInvoice.amount_paid, existingInvoice.currency)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-red-600">
                      <span>{t('invoice.balanceDue')}</span>
                      <span>{formatCurrency(existingInvoice.grand_total - existingInvoice.amount_paid, existingInvoice.currency)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Bank Details */}
            <div className="border-t pt-6 mt-6">
              <h3 className="font-semibold mb-4 text-lg">{t('invoice.bankDetails')}</h3>
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
                  {invoiceSettings?.bank_name && (
                    <div className="flex justify-between sm:block">
                      <span className="text-muted-foreground font-medium">{t('invoice.bankName')}</span>
                      <span className="font-semibold sm:block sm:mt-1">{invoiceSettings.bank_name}</span>
                    </div>
                  )}
                  {invoiceSettings?.branch_identifier && (
                    <div className="flex justify-between sm:block">
                      <span className="text-muted-foreground font-medium">{invoiceSettings.branch_identifier_label || 'SWIFT/BIC'}</span>
                      <span className="font-semibold font-mono sm:block sm:mt-1">{invoiceSettings.branch_identifier}</span>
                    </div>
                  )}
                  {invoiceSettings?.bank_address && (
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground font-medium">{t('invoice.bankAddress')}</span>
                      <span className="font-semibold sm:block sm:mt-1 whitespace-pre-line">{invoiceSettings.bank_address}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            {existingInvoice.notes && (
              <div className="border-t pt-6 mt-6">
                <h3 className="font-semibold mb-2">{t('invoice.notes')}</h3>
                <p className="text-sm text-muted-foreground">{existingInvoice.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Update Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('invoice.updatePayment')}</DialogTitle>
              <DialogDescription>{t('invoice.updatePaymentDesc')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('invoice.paymentStatus')}</Label>
                <Select
                  value={paymentData.payment_status}
                  onValueChange={(v) => setPaymentData({...paymentData, payment_status: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_paid">{t('invoice.notPaid')}</SelectItem>
                    <SelectItem value="partial">{t('invoice.partial')}</SelectItem>
                    <SelectItem value="paid">{t('invoice.paid')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('invoice.amountPaid')}</Label>
                <Input
                  type="number"
                  value={paymentData.amount_paid}
                  onChange={(e) => setPaymentData({...paymentData, amount_paid: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('invoice.paymentDate')}</Label>
                <Input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('invoice.paymentNotes')}</Label>
                <Textarea
                  value={paymentData.payment_notes}
                  onChange={(e) => setPaymentData({...paymentData, payment_notes: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handlePaymentUpdate}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Create/Edit mode
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/sales')}>
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{t('common.back')}</span>
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">
              {existingInvoice ? t('invoice.editInvoice') : t('invoice.createInvoice')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {packageData ? `${t('invoice.forPackage')}: ${packageData.client_name}` : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {existingInvoice && (
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              {t('common.cancel')}
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="flex-1 sm:flex-none">
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Invoice Form */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Client Information */}
          <Card>
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-base sm:text-lg">{t('invoice.clientInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">{t('invoice.clientName')} *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t('invoice.clientEmail')} *</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">{t('invoice.clientAddress')}</Label>
                <Textarea
                  value={formData.client_address}
                  onChange={(e) => setFormData({...formData, client_address: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label className="text-sm">{t('invoice.tripDescription')} *</Label>
                <Input
                  value={formData.trip_description}
                  onChange={(e) => setFormData({...formData, trip_description: e.target.value})}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between p-4 sm:p-6">
              <CardTitle className="text-base sm:text-lg">{t('invoice.lineItems')}</CardTitle>
              <Button size="sm" onClick={addLineItem}>
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('invoice.addItem')}</span>
              </Button>
            </CardHeader>
            <CardContent className="p-0 sm:p-6 sm:pt-0">
              <div className="overflow-x-auto">
                <Table className="min-w-[600px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%] text-xs sm:text-sm">{t('invoice.description')}</TableHead>
                      <TableHead className="w-[10%] text-xs sm:text-sm">{t('invoice.qty')}</TableHead>
                      <TableHead className="w-[15%] text-xs sm:text-sm">{t('invoice.unitPrice')}</TableHead>
                      <TableHead className="w-[10%] text-xs sm:text-sm">{t('invoice.vatPercent')}</TableHead>
                      <TableHead className="w-[15%] text-xs sm:text-sm">{t('invoice.vatAmount')}</TableHead>
                      <TableHead className="w-[15%] text-xs sm:text-sm">{t('invoice.total')}</TableHead>
                      <TableHead className="w-[5%]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="p-2">
                          <Input
                            value={item.description}
                            onChange={(e) => handleLineItemChange(idx, 'description', e.target.value)}
                            placeholder={t('invoice.itemDescription')}
                            className="text-sm"
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleLineItemChange(idx, 'quantity', parseFloat(e.target.value) || 1)}
                            className="text-sm"
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => handleLineItemChange(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="text-sm"
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={item.vat_percent}
                            onChange={(e) => handleLineItemChange(idx, 'vat_percent', parseFloat(e.target.value) || 0)}
                            className="text-sm"
                          />
                        </TableCell>
                        <TableCell className="text-right text-sm p-2">
                          {formatCurrency(item.vat_amount, formData.currency)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-sm p-2">
                          {formatCurrency(item.total, formData.currency)}
                        </TableCell>
                        <TableCell className="p-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(idx)}
                            disabled={lineItems.length === 1}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-base sm:text-lg">{t('invoice.notes')}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder={t('invoice.notesPlaceholder')}
                rows={3}
              />
            </CardContent>
          </Card>
        </div>

        {/* Summary Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {/* Invoice Details */}
          <Card>
            <CardHeader>
              <CardTitle>{t('invoice.invoiceDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t('invoice.currency')}</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(v) => setFormData({...formData, currency: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('invoice.invoiceDate')}</Label>
                <Input
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('invoice.dueDate')}</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Totals */}
          <Card>
            <CardHeader>
              <CardTitle>{t('invoice.summary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('invoice.subtotal')}</span>
                <span>{formatCurrency(subtotal, formData.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('invoice.totalVat')}</span>
                <span>{formatCurrency(totalVat, formData.currency)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-3">
                <span>{t('invoice.grandTotal')}</span>
                <span className="text-primary">{formatCurrency(grandTotal, formData.currency)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Company Info Preview */}
          {invoiceSettings?.company_name && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('invoice.from')}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{invoiceSettings.company_name}</p>
                {invoiceSettings.company_address && <p className="whitespace-pre-line">{invoiceSettings.company_address}</p>}
                {invoiceSettings.company_email && <p>{invoiceSettings.company_email}</p>}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Invoices;
